/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { memo } from "react";
import type { IconProps } from "./types";

/** Shield checkmark icon — used for "verified safe" labels */
export const ShieldCheckIcon: React.FC<IconProps> = memo(
  ({ width = "15", height = "15", stroke = "#000000a8", className }) => (
    <svg
      stroke={stroke}
      fill={stroke}
      strokeWidth="0"
      viewBox="0 0 512 512"
      height={height}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="50"
        d="M336 176 225.2 304 176 255.8"
      />
      <path
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="50"
        d="M463.1 112.37C373.68 96.33 336.71 84.45 256 48c-80.71 36.45-117.68 48.33-207.1 64.37C32.7 369.13 240.58 457.79 256 464c15.42-6.21 223.3-94.87 207.1-351.63z"
      />
    </svg>
  ),
);
ShieldCheckIcon.displayName = "ShieldCheckIcon";

/** Green circle checkmark — success indicator */
export const SuccessCircleIcon: React.FC<IconProps> = memo(
  ({ width = "12", height = "12", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 122.88 122.88"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fill="#019901"
        d="M34.388,67.984c-0.286-0.308-0.542-0.638-0.762-0.981c-0.221-0.345-0.414-0.714-0.573-1.097 c-0.531-1.265-0.675-2.631-0.451-3.934c0.224-1.294,0.812-2.531,1.744-3.548l0.34-0.35c2.293-2.185,5.771-2.592,8.499-0.951 c0.39,0.233,0.762,0.51,1.109,0.827l0.034,0.031c1.931,1.852,5.198,4.881,7.343,6.79l1.841,1.651l22.532-23.635 c0.317-0.327,0.666-0.62,1.035-0.876c0.378-0.261,0.775-0.482,1.185-0.661c0.414-0.181,0.852-0.323,1.3-0.421 c0.447-0.099,0.903-0.155,1.356-0.165h0.026c0.451-0.005,0.893,0.027,1.341,0.103c0.437,0.074,0.876,0.193,1.333,0.369 c0.421,0.161,0.825,0.363,1.207,0.604c0.365,0.231,0.721,0.506,1.056,0.822l0.162,0.147c0.316,0.313,0.601,0.653,0.85,1.014 c0.256,0.369,0.475,0.766,0.652,1.178c0.183,0.414,0.325,0.852,0.424,1.299c0.1,0.439,0.154,0.895,0.165,1.36v0.23 c-0.004,0.399-0.042,0.804-0.114,1.204c-0.079,0.435-0.198,0.863-0.356,1.271c-0.16,0.418-0.365,0.825-0.607,1.21 c-0.238,0.377-0.518,0.739-0.832,1.07l-27.219,28.56c-0.32,0.342-0.663,0.642-1.022,0.898c-0.369,0.264-0.767,0.491-1.183,0.681 c-0.417,0.188-0.851,0.337-1.288,0.44c-0.435,0.104-0.889,0.166-1.35,0.187l-0.125,0.003c-0.423,0.009-0.84-0.016-1.241-0.078 l-0.102-0.02c-0.415-0.07-0.819-0.174-1.205-0.31c-0.421-0.15-0.833-0.343-1.226-0.575l-0.063-0.04 c-0.371-0.224-0.717-0.477-1.032-0.754l-0.063-0.06c-1.58-1.466-3.297-2.958-5.033-4.466c-3.007-2.613-7.178-6.382-9.678-9.02 L34.388,67.984L34.388,67.984z M61.44,0c16.96,0,32.328,6.883,43.453,17.987c11.104,11.125,17.986,26.493,17.986,43.453 c0,16.961-6.883,32.329-17.986,43.454C93.769,115.998,78.4,122.88,61.44,122.88c-16.961,0-32.329-6.882-43.454-17.986 C6.882,93.769,0,78.4,0,61.439C0,44.48,6.882,29.112,17.986,17.987C29.112,6.883,44.479,0,61.44,0L61.44,0z M96.899,25.981 C87.826,16.907,75.29,11.296,61.44,11.296c-13.851,0-26.387,5.611-35.46,14.685c-9.073,9.073-14.684,21.609-14.684,35.458 c0,13.851,5.611,26.387,14.684,35.46s21.609,14.685,35.46,14.685c13.85,0,26.386-5.611,35.459-14.685s14.684-21.609,14.684-35.46 C111.583,47.59,105.973,35.054,96.899,25.981L96.899,25.981z"
      />
    </svg>
  ),
);
SuccessCircleIcon.displayName = "SuccessCircleIcon";

/** Red circle X — error indicator */
export const ErrorCircleIcon: React.FC<IconProps> = memo(
  ({ width = "12", height = "12", className }) => (
    <svg
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g fillRule="evenodd">
        <path
          d="m0,5.95a5.95,5.95 0 1 1 11.9,0a5.95,5.95 0 0 1 -11.9,0z"
          fill="#DD2B0E"
        />
        <path
          d="m11.05,5.95a5.1,5.1 0 1 0 -10.2,0a5.1,5.1 0 0 0 10.2,0z"
          fill="#fff"
        />
        <path
          d="m5.95,5.07l-1.19,-1.19a0.25,0.24 0 0 0 -0.35,0l-0.53,0.52a0.25,0.24 0 0 0 0.01,0.35l1.18,1.19l-1.19,1.19a0.25,0.24 0 0 0 0,0.35l0.53,0.52c0.09,0.09 0.26,0.1 0.35,0l1.19,-1.19l1.19,1.19a0.25,0.24 0 0 0 0.35,0l0.53,-0.52a0.25,0.24 0 0 0 0,-0.35l-1.19,-1.19l1.19,-1.19a0.25,0.24 0 0 0 0,-0.35l-0.53,-0.52a0.25,0.24 0 0 0 -0.35,0l-1.19,1.19z"
          fill="#DD2B0E"
        />
      </g>
    </svg>
  ),
);
ErrorCircleIcon.displayName = "ErrorCircleIcon";

/**
 * Star icon for review ratings. Supports full, half, and empty states via the
 * `variant` prop so a fractional average (e.g. 4.6) can be drawn precisely.
 * Decorative by default (aria-hidden) — the surrounding StarRating component
 * provides the accessible text label.
 */
interface StarIconProps extends IconProps {
  variant?: "full" | "half" | "empty";
}

export const StarIcon: React.FC<StarIconProps> = memo(
  ({ width = "18", height = "18", className, variant = "full" }) => {
    // A stable unique id per render is needed so multiple half-stars on the
    // same page do not share (and clobber) one another's gradient definition.
    const gradientId = React.useId();
    const filledColor = "#f5a623"; // amber — standard rating star colour
    const emptyColor = "#d8d8d8";

    let fill: string;
    if (variant === "full") {
      fill = filledColor;
    } else if (variant === "empty") {
      fill = emptyColor;
    } else {
      fill = `url(#${gradientId})`;
    }

    return (
      <svg
        width={width}
        height={height}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden="true"
        focusable="false"
      >
        {variant === "half" && (
          <defs>
            <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
              <stop offset="50%" stopColor={filledColor} />
              <stop offset="50%" stopColor={emptyColor} />
            </linearGradient>
          </defs>
        )}
        <path
          fill={fill}
          d="M12 2.25l2.955 5.988 6.608.96-4.781 4.66 1.128 6.582L12 17.34l-5.91 3.1 1.128-6.582-4.781-4.66 6.608-.96L12 2.25z"
        />
      </svg>
    );
  },
);
StarIcon.displayName = "StarIcon";
